import React, { useEffect, useMemo } from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  ShippingCartItem,
  ShippingCartItemViewProps,
  ShippingCartType,
} from "./ShippingCart.types";
import {
  FieldError,
  FullWidthFieldContainer,
  RowContainer,
  ShortFieldContainer,
  ShortTextField,
  SingleItemRowContainer,
  getMuiLocalizationProviderAdapterName,
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en"; // US
import "dayjs/locale/en-gb"; // UK
import "dayjs/locale/en-ca"; // CA

import {
  CountryCode,
  DimensionUnit,
  ValidationError,
  ValidationErrorEntity,
  WeightUnit,
  addFieldError,
  getFieldError,
  removeFieldError,
} from "packable-common";

import "./ShippingCart.css";
import {
  validateBoxDimensions,
  validateBoxWeight,
} from "../../utils/validator/ItemValidationUtils";
import { DimensionName } from "../../utils/validator/Validators.types";

const getStyles = (theme: Theme) => ({
  ShippingCartItemViewContainer: {
    width: "100%",
    pb: 2,
  },
  ShippingCartItemViewContainerDivider: {
    mb: 2,
  },
  ShippingCartItemViewContentSectionTitleContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
    padding: 1,
  },
  ShippingCartItemViewContentSectionTitle: {
    muiVariant: "h6",
  },
  ShippingCartItemViewContentSectionTitleDivider: {
    width: "100%",
    marginBottom: 2,
  },
  ShippingCartItemViewContentActionRow: {
    padding: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  ShippingCartItemViewContentActionRemoveButton: {
    muiVariant: "outlined",
    muiColor: "secondary",
  },
});

export const ShippingCartItemView: React.FC<ShippingCartItemViewProps> = (
  props
): React.JSX.Element => {
  const {
    shippingCartItem,
    shippingCartType,
    multipleItems = false,
    onShippingCartItemUpdate,
    validationErrors,
    onValidationErrorsUpdate,
    onRemoveShippingCartItem,
    userAppState,
    styles,
  } = props;

  logDebug("ShippingCartItemView PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useMemo(() => getStyles(theme), [theme]);
  const effectiveStyles = useMemo(
    () =>
      styles === undefined
        ? defaultStyles
        : mergeProperties(defaultStyles, styles),
    [defaultStyles, styles]
  );

  logDebug("ShippingCartItemView effectiveStyles");
  logDebug(effectiveStyles);

  const setValidationError = (
    fieldName: string,
    validationError: string
  ): void => {
    onValidationErrorsUpdate(
      addFieldError(
        new ValidationErrorEntity(fieldName, validationError),
        validationErrors
      )
    );
  };

  const setValidationErrorObject = (validationError: ValidationError): void => {
    onValidationErrorsUpdate(addFieldError(validationError, validationErrors));
  };

  const removeValidationError = (fieldName: string): void => {
    onValidationErrorsUpdate(removeFieldError(fieldName, validationErrors));
  };

  const validateEndStorageDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 30);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 365);
    if (
      shippingCartItem.storageEndDate !== undefined &&
      shippingCartItem.storageEndDate !== null
    ) {
      if (
        shippingCartItem.storageEndDate < minDate ||
        shippingCartItem.storageEndDate > maxDate
      ) {
        setValidationError(
          "storageEndDate",
          "We offer storage periods from 1 month up to 12 months"
        );
      } else {
        removeValidationError("storageEndDate");
      }
    }
  };

  const validateShippingCartItem = () => {
    validateBoxDimensions(
      userAppState?.countryCode || CountryCode.US,
      "itemLabel",
      {
        name: DimensionName.LENGTH,
        fieldName: "itemLength",
        value: shippingCartItem.itemLength,
      },
      {
        name: DimensionName.WIDTH,
        fieldName: "itemWidth",
        value: shippingCartItem.itemWidth,
      },
      {
        name: DimensionName.HEIGHT,
        fieldName: "itemHeight",
        value: shippingCartItem.itemHeight,
      },
      setValidationErrorObject,
      removeValidationError
    );
    validateBoxWeight(
      userAppState?.countryCode || CountryCode.US,
      {
        name: "weight",
        fieldName: "itemWeight",
        value: shippingCartItem.itemWeight
      },
      setValidationErrorObject,
      removeValidationError
    );
    validateEndStorageDate();
  };

  const handleUseTheSameDateValueChange = (event: any) => {
    let updatedShippingCartItem = packableDeepClone(shippingCartItem);
    updatedShippingCartItem.useTheSameDateAndTime =
      !updatedShippingCartItem.useTheSameDateAndTime;
    onShippingCartItemUpdate(updatedShippingCartItem);
  };

  const onDateChange = (event: any) => {
    if (event) {
      if (event instanceof dayjs) {
        let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
        updatedCart.storageEndDate = (event as Dayjs).toDate();
        onShippingCartItemUpdate(updatedCart);
      }
    }
  };

  const onLengthChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemLength = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onWidthChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemWidth = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onHeightChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemHeight = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onWeightChange = (event: any) => {
    let updatedCart: ShippingCartItem = packableDeepClone(shippingCartItem);
    updatedCart.itemWeight = event.target.value;
    onShippingCartItemUpdate(updatedCart);
  };

  const onRemoveItem = () => {
    onRemoveShippingCartItem(shippingCartItem.itemId);
  };

  const getDimensionUnit = () => {
    return userAppState?.units?.dimensionUnit === DimensionUnit.INCH
      ? "in"
      : "cm";
  };

  const getWeightUnit = () => {
    return userAppState?.units?.weightUnit === WeightUnit.POUND ? "lb" : "kg";
  };

  const minDate = dayjs().add(30, "day");
  const maxDate = dayjs().add(1, "year");

  useEffect(() => {
    validateShippingCartItem();
  }, [
    shippingCartItem.itemWeight,
    shippingCartItem.itemLength,
    shippingCartItem.itemWidth,
    shippingCartItem.itemHeight,
    shippingCartItem.storageEndDate,
  ]);

  return (
    <>
      <Grid
        className="ShippingCartItemViewContainer"
        sx={effectiveStyles.ShippingCartItemViewContainer}
      >
        <Divider
          className="ShippingCartItemViewContainerDivider"
          sx={effectiveStyles.ShippingCartItemViewContainerDivider}
        />
        {shippingCartType === ShippingCartType.HOME ? (
          <SingleItemRowContainer>
            <ShortFieldContainer>
              <ShortTextField
                id="itemLabel"
                label="Label"
                value={shippingCartItem.itemLabel}
                disabled
                errorMessage={
                  getFieldError("itemLabel", validationErrors)?.errorMessage
                }
              />
            </ShortFieldContainer>
          </SingleItemRowContainer>
        ) : (
          <>
            {multipleItems && (
              <RowContainer>
                <FullWidthFieldContainer>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={shippingCartItem.useTheSameDateAndTime}
                        onChange={handleUseTheSameDateValueChange}
                      />
                    }
                    label="Use the same Date and Time for all items"
                  />
                </FullWidthFieldContainer>
              </RowContainer>
            )}
            <RowContainer>
              <ShortFieldContainer>
                <ShortTextField
                  id="itemLabel"
                  label="Label"
                  value={shippingCartItem.itemLabel}
                  disabled
                  errorMessage={
                    getFieldError("itemLabel", validationErrors)?.errorMessage
                  }
                />
              </ShortFieldContainer>
              <ShortFieldContainer>
                <Grid sx={{ display: "flex", flexDirection: "column" }}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={getMuiLocalizationProviderAdapterName()}
                  >
                    <DatePicker
                      sx={{ width: "240px" }}
                      label="Store Until"
                      value={dayjs(
                        shippingCartItem.storageEndDate || new Date()
                      )}
                      onChange={onDateChange}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </LocalizationProvider>
                  <FieldError
                    errorMessage={
                      getFieldError("storageEndDate", validationErrors)
                        ?.errorMessage
                    }
                  />
                </Grid>
              </ShortFieldContainer>
            </RowContainer>
            <RowContainer>
              <ShortFieldContainer>
                <ShortTextField
                  id="itemLength"
                  label="Length"
                  value={shippingCartItem.itemLength}
                  onChange={onLengthChange}
                  endAdornmentText={getDimensionUnit()}
                  errorMessage={
                    getFieldError("itemLength", validationErrors)?.errorMessage
                  }
                />
              </ShortFieldContainer>
              <ShortFieldContainer>
                <ShortTextField
                  id="itemWidth"
                  label="Width"
                  value={shippingCartItem.itemWidth}
                  onChange={onWidthChange}
                  endAdornmentText={getDimensionUnit()}
                  errorMessage={
                    getFieldError("itemWidth", validationErrors)?.errorMessage
                  }
                />
              </ShortFieldContainer>
            </RowContainer>
            <RowContainer>
              <ShortFieldContainer>
                <ShortTextField
                  id="itemHeight"
                  label="Height"
                  value={shippingCartItem.itemHeight}
                  onChange={onHeightChange}
                  endAdornmentText={getDimensionUnit()}
                  errorMessage={
                    getFieldError("itemHeight", validationErrors)?.errorMessage
                  }
                />
              </ShortFieldContainer>
              <ShortFieldContainer>
                <ShortTextField
                  id="itemWeight"
                  label="Weight"
                  value={shippingCartItem.itemWeight}
                  onChange={onWeightChange}
                  endAdornmentText={getWeightUnit()}
                  errorMessage={
                    getFieldError("itemWeight", validationErrors)?.errorMessage
                  }
                />
              </ShortFieldContainer>
            </RowContainer>
          </>
        )}
        <Grid
          className="ShippingCartItemViewContentActionRow"
          sx={effectiveStyles.ShippingCartItemViewContentActionRow}
        >
          <Button
            className="ShippingCartItemViewContentActionRemoveButton"
            sx={effectiveStyles.ShippingCartItemViewContentActionRemoveButton}
            variant={
              effectiveStyles.ShippingCartItemViewContentActionRemoveButton
                .muiVariant
            }
            color={
              effectiveStyles.ShippingCartItemViewContentActionRemoveButton
                .muiColor
            }
            onClick={onRemoveItem}
          >
            Remove from Cart
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
